/**
 * PersonaPicker Modal Component
 * 
 * Allows users to browse and select AI assistant personas.
 * Organized by category with search and filtering.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Spinner, Button, Input } from '@/components/ui';
import { logger } from '../../utils/logger';

// Inline SVG icons to avoid external dependency
const XIcon = ({ className }: { className?: string }) => (
  <svg className={className || "w-5 h-5"} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const SearchIconSvg = ({ className }: { className?: string }) => (
  <svg className={className || "w-5 h-5"} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const CheckIconSvg = ({ className }: { className?: string }) => (
  <svg className={className || "w-5 h-5"} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const ChevronRightIconSvg = ({ className }: { className?: string }) => (
  <svg className={className || "w-5 h-5"} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const SparklesIcon = ({ className }: { className?: string }) => (
  <svg className={className || "w-5 h-5"} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
  </svg>
);

const PlaneIcon = ({ className }: { className?: string }) => (
  <svg className={className || "w-5 h-5"} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
  </svg>
);

const BookOpenIcon = ({ className }: { className?: string }) => (
  <svg className={className || "w-5 h-5"} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
  </svg>
);

const GraduationCapIcon = ({ className }: { className?: string }) => (
  <svg className={className || "w-5 h-5"} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14v7" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9v2a1 1 0 001 1h1" />
  </svg>
);

const FileTextIconSvg = ({ className }: { className?: string }) => (
  <svg className={className || "w-5 h-5"} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const UserIcon = ({ className }: { className?: string }) => (
  <svg className={className || "w-5 h-5"} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

// Types matching backend
interface PersonaMetadata {
  personaId: string;
  category: string;
  name: string;
  description?: string;
  industryClassification: 'regulated' | 'professional' | 'creative';
  isBuiltIn: boolean;
  isVariant: boolean;
  parentId?: string;
  icon?: string;
  color?: string;
}

interface PersonaCategory {
  id: string;
  name: string;
  description: string;
}

interface PersonaPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (personaId: string) => void;
  currentPersonaId?: string;
  title?: string;
}

/**
 * Get icon component for a persona
 */
function getPersonaIcon(icon?: string, className = 'w-5 h-5') {
  switch (icon) {
    case 'plane':
      return <PlaneIcon className={className} />;
    case 'cpu':
      return <SparklesIcon className={className} />;
    case 'book-open':
      return <BookOpenIcon className={className} />;
    case 'search':
      return <SearchIconSvg className={className} />;
    case 'rocket':
      return <SparklesIcon className={className} />;
    case 'file-text':
      return <FileTextIconSvg className={className} />;
    case 'graduation-cap':
      return <GraduationCapIcon className={className} />;
    default:
      return <UserIcon className={className} />;
  }
}

/**
 * Category icons
 */
function getCategoryIcon(categoryId: string, className = 'w-5 h-5') {
  switch (categoryId) {
    case 'technical':
      return <PlaneIcon className={className} />;
    case 'creative':
      return <BookOpenIcon className={className} />;
    case 'academic':
      return <GraduationCapIcon className={className} />;
    case 'business':
      return <FileTextIconSvg className={className} />;
    default:
      return <UserIcon className={className} />;
  }
}

/**
 * Industry classification badge
 */
function IndustryBadge({ classification }: { classification: string }) {
  const colors = {
    regulated: 'bg-destructive-light text-destructive',
    professional: 'bg-info-light text-info',
    creative: 'bg-secondary text-secondary-foreground',
  };

  const labels = {
    regulated: 'Regulated',
    professional: 'Professional',
    creative: 'Creative',
  };

  return (
    <span className={`text-xs px-2 py-0.5 rounded-full ${colors[classification as keyof typeof colors] || colors.professional}`}>
      {labels[classification as keyof typeof labels] || classification}
    </span>
  );
}

/**
 * PersonaPicker Modal
 */
export function PersonaPicker({ 
  isOpen, 
  onClose, 
  onSelect, 
  currentPersonaId,
  title = 'Select AI Assistant Persona'
}: PersonaPickerProps) {
  const [personas, setPersonas] = useState<PersonaMetadata[]>([]);
  const [categories, setCategories] = useState<PersonaCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch personas and categories
  useEffect(() => {
    if (!isOpen) return;

    async function fetchData() {
      setIsLoading(true);
      setError(null);

      try {
        // Mock data — in production, personas (including color hex values) come from the backend API.
        // Persona colors are intentionally not tokenized; they are runtime-supplied hex strings.
        const mockCategories: PersonaCategory[] = [
          { id: 'technical', name: 'Technical', description: 'Engineering and technical documentation' },
          { id: 'creative', name: 'Creative', description: 'Fiction and creative writing' },
          { id: 'academic', name: 'Academic', description: 'Research and scholarly writing' },
          { id: 'business', name: 'Business', description: 'Business and process documentation' },
        ];

        const mockPersonas: PersonaMetadata[] = [
          {
            personaId: 'aerospace-engineer',
            category: 'technical',
            name: 'Aerospace Engineer',
            description: 'Avionics certification specialist for FAA/EASA compliance',
            industryClassification: 'regulated',
            isBuiltIn: true,
            isVariant: false,
            icon: 'plane',
            color: '#1e40af',
          },
          {
            personaId: 'aerospace-avionics',
            category: 'technical',
            name: 'Avionics Software Engineer',
            description: 'DO-178C software certification specialist',
            industryClassification: 'regulated',
            isBuiltIn: true,
            isVariant: true,
            parentId: 'aerospace-engineer',
            icon: 'cpu',
            color: '#1e3a8a',
          },
          {
            personaId: 'fiction-writer',
            category: 'creative',
            name: 'Fiction Writer',
            description: 'Creative writing assistant for novels and stories',
            industryClassification: 'creative',
            isBuiltIn: true,
            isVariant: false,
            icon: 'book-open',
            color: '#7c3aed',
          },
          {
            personaId: 'fiction-mystery',
            category: 'creative',
            name: 'Mystery Writer',
            description: 'Specialist in mystery, thriller, and suspense fiction',
            industryClassification: 'creative',
            isBuiltIn: true,
            isVariant: true,
            parentId: 'fiction-writer',
            icon: 'search',
            color: '#6d28d9',
          },
          {
            personaId: 'fiction-scifi',
            category: 'creative',
            name: 'Science Fiction Writer',
            description: 'Specialist in science fiction and speculative fiction',
            industryClassification: 'creative',
            isBuiltIn: true,
            isVariant: true,
            parentId: 'fiction-writer',
            icon: 'rocket',
            color: '#4f46e5',
          },
          {
            personaId: 'technical-writer',
            category: 'technical',
            name: 'Technical Writer',
            description: 'Professional documentation and user manual specialist',
            industryClassification: 'professional',
            isBuiltIn: true,
            isVariant: false,
            icon: 'file-text',
            color: '#059669',
          },
          {
            personaId: 'phd-researcher',
            category: 'academic',
            name: 'PhD Researcher',
            description: 'Academic writing and research assistant',
            industryClassification: 'creative',
            isBuiltIn: true,
            isVariant: false,
            icon: 'graduation-cap',
            color: '#dc2626',
          },
        ];

        setCategories(mockCategories);
        setPersonas(mockPersonas);
      } catch (err) {
        setError('Failed to load personas');
        logger.error('Error loading personas:', err);
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, [isOpen]);

  // Filter personas by category and search
  const filteredPersonas = useMemo(() => {
    return personas.filter(persona => {
      // Category filter
      if (selectedCategory && persona.category !== selectedCategory) {
        return false;
      }

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          persona.name.toLowerCase().includes(query) ||
          persona.description?.toLowerCase().includes(query) ||
          persona.category.toLowerCase().includes(query)
        );
      }

      return true;
    });
  }, [personas, selectedCategory, searchQuery]);

  // Group personas by parent for variant display
  const groupedPersonas = useMemo(() => {
    const groups: Map<string | undefined, PersonaMetadata[]> = new Map();
    
    // First, separate base personas and variants
    const basePersonas = filteredPersonas.filter(p => !p.isVariant);
    const variants = filteredPersonas.filter(p => p.isVariant);

    // Add base personas to groups
    for (const persona of basePersonas) {
      groups.set(persona.personaId, [persona]);
    }

    // Add variants to their parent groups
    for (const variant of variants) {
      const group = groups.get(variant.parentId);
      if (group) {
        group.push(variant);
      } else {
        // Orphan variant - create new group
        groups.set(variant.personaId, [variant]);
      }
    }

    return Array.from(groups.values());
  }, [filteredPersonas]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh]">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal - Fixed height to prevent resizing on category change */}
      <div className="relative bg-card rounded-xl shadow-2xl w-full max-w-3xl h-[70vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">{title}</h2>
          <Button
            onClick={onClose}
            variant="ghost"
            size="icon"
          >
            <XIcon className="w-5 h-5 text-muted-foreground" />
          </Button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-border">
          <div className="relative">
            <SearchIconSvg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search personas..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-10"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Category sidebar */}
          <div className="w-48 border-r border-border overflow-y-auto">
            <div className="p-2">
              <Button
                onClick={() => setSelectedCategory(null)}
                variant="ghost"
                className={`w-full flex items-center gap-2 px-3 py-2 justify-start h-auto ${
                  selectedCategory === null
                    ? 'bg-info-light text-info'
                    : 'text-foreground'
                }`}
              >
                <SparklesIcon className="w-4 h-4" />
                <span className="text-sm font-medium">All Personas</span>
              </Button>

              {categories.map(category => (
                <Button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  variant="ghost"
                  className={`w-full flex items-center gap-2 px-3 py-2 justify-start h-auto ${
                    selectedCategory === category.id
                      ? 'bg-info-light text-info'
                      : 'text-foreground'
                  }`}
                >
                  {getCategoryIcon(category.id, 'w-4 h-4')}
                  <span className="text-sm font-medium">{category.name}</span>
                </Button>
              ))}
            </div>
          </div>

          {/* Persona list */}
          <div className="flex-1 overflow-y-auto p-4">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <Spinner size="xl" className="text-link" />
              </div>
            ) : error ? (
              <div className="text-center text-destructive py-8">{error}</div>
            ) : groupedPersonas.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">No personas found</div>
            ) : (
              <div className="space-y-4">
                {groupedPersonas.map(group => (
                  <div key={group[0].personaId} className="space-y-2">
                    {group.map((persona, index) => (
                      <Button
                        key={persona.personaId}
                        onClick={() => {
                          onSelect(persona.personaId);
                          onClose();
                        }}
                        variant="outline"
                        className={`w-full flex items-start gap-3 p-3 h-auto justify-start ${
                          currentPersonaId === persona.personaId
                            ? 'border-primary bg-info-light'
                            : 'hover:border-primary'
                        } ${index > 0 ? 'ml-6' : ''}`}
                      >
                        {/* Icon */}
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                          // eslint-disable-next-line no-restricted-syntax -- Dynamic background color based on persona.color (API-supplied hex)
                          style={{ backgroundColor: persona.color ? `${persona.color}20` : 'hsl(var(--muted))' }}
                        >
                          {/* eslint-disable-next-line no-restricted-syntax -- Dynamic text color based on persona.color (API-supplied hex) */}
                          <span style={{ color: persona.color || 'hsl(var(--muted-foreground))' }}>
                            {getPersonaIcon(persona.icon)}
                          </span>
                        </div>

                        {/* Content */}
                        <div className="flex-1 text-left">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-foreground">
                              {persona.name}
                            </span>
                            {persona.isVariant && (
                              <ChevronRightIconSvg className="w-3 h-3 text-muted-foreground" />
                            )}
                            <IndustryBadge classification={persona.industryClassification} />
                          </div>
                          <p className="text-sm text-muted-foreground mt-0.5">
                            {persona.description}
                          </p>
                        </div>

                        {/* Selected indicator */}
                        {currentPersonaId === persona.personaId && (
                          <CheckIconSvg className="w-5 h-5 text-link flex-shrink-0" />
                        )}
                      </Button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border bg-muted/50">
          <p className="text-xs text-muted-foreground text-center">
            Personas define the AI assistant's identity, expertise, and available tools.
            <br />
            <span className="text-destructive">Regulated</span> personas have locked temperature for compliance.
          </p>
        </div>
      </div>
    </div>
  );
}

export default PersonaPicker;
